import React, { useState, useMemo } from 'react';
import { Typography, TextField, Button, BaseIcon } from '@scale/scaleui';
import { JobPostTable } from './JobPostTable';
import { useQuery } from 'react-query';
import { useAllActiveSignupFlowsQuery } from 'react-scale/corp/lib/api/growth';
import { useAllGreenhouseJobPostsQuery } from 'react-scale/corp/lib/api/greenhouse';
import SAlert from 'react-s-alert';
import { JobPostStatus, JobTableData } from '@scale/shared-types';
import {
  SignupFlowInfo,
  findOCPWorkflows,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { Link } from 'react-router-dom';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { useFetchReferralRewardsConfig } from 'react-scale/hooks/referrals/useFetchReferralRewardsConfig';
import FilterComponent from 'react-scale/components/GrowthCenter/components/FilterComponent';
import SelectTag, {
  JobPostType,
} from 'react-scale/components/GrowthCenter/components/SelectJobPostTag';

const JobPostPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterTerms, setFilterTerms] = useState<{ value: string; label: string }[]>([]);

  const { data: signupFlowData, error: signupFlowDataError } = useAllActiveSignupFlowsQuery();
  const signupFlowIdToData = useMemo(() => {
    return (signupFlowData ?? []).reduce((acc, signupFlow: SignupFlowInfo) => {
      acc[signupFlow._id] = {
        name: signupFlow.name,
        onboardingFlow: signupFlow.onboardingFlow ?? '',
      };
      return acc;
    }, {} as Record<string, { name: string; onboardingFlow: string }>);
  }, [signupFlowData]);

  const { data: ocpWorkflows, error: ocpWorkflowsError } = useQuery(
    ['onboarding-flows'],
    findOCPWorkflows,
  );
  const ocpFlowIdToName = useMemo(() => {
    return (ocpWorkflows ?? []).reduce((acc, ocpFlow) => {
      acc[ocpFlow._id] = ocpFlow.name;
      return acc;
    }, {} as Record<string, string>);
  }, [ocpWorkflows]);

  const { data: referralRewardData, error: referralRewardError } = useFetchReferralRewardsConfig();

  const {
    data: greenhouseJobPostData,
    error: greenhouseJobPostError,
    isLoading: greenhouseJobPostDataLoading,
  } = useAllGreenhouseJobPostsQuery(); // Entries from Greenhouse

  if (greenhouseJobPostError || signupFlowDataError || referralRewardError || ocpWorkflowsError) {
    const errorMessage = (greenhouseJobPostError as { response?: { message?: string } }).response
      ?.message;
    SAlert.error(errorMessage ? `Failed to find jobs: ${errorMessage}` : 'Failed to find jobs', {
      timeout: 5000,
    });
  }

  const processedData = useMemo(() => {
    return (greenhouseJobPostData ?? [])
      .map(jobPost => {
        if (!jobPost.title) {
          return null;
        }
        const reward =
          // @ts-ignore - Treating map as an object to access values
          referralRewardData?.greenhouseJobIdToAmountHundredthsOfCents?.[jobPost.job_id] ?? 0;
        const processed: JobTableData = {
          id: jobPost.id.toString(),
          name: jobPost.title,
          pod: jobPost.podGroup,
          status: jobPost.live
            ? JobPostStatus.Public
            : jobPost.isActive
            ? JobPostStatus.Private
            : JobPostStatus.Inactive,
          ocpFlow: jobPost.signupFlowId
            ? ocpFlowIdToName[signupFlowIdToData[jobPost.signupFlowId]?.onboardingFlow]
            : undefined,
          signupFlow: jobPost.signupFlowId ? signupFlowIdToData[jobPost.signupFlowId] : undefined,
          jobName: jobPost.job_name, // Job name, not job post name
          jobId: jobPost.job_id.toString(),
          referralReward: reward
            ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(reward / 10000)
            : ' ', // Space comes before $ in ASCII, to make sorting work
        };
        return processed;
      })
      .filter((item): item is JobTableData => item !== null);
  }, [greenhouseJobPostData, referralRewardData, ocpFlowIdToName, signupFlowIdToData]);

  const filteredData = useMemo(() => {
    if (!processedData) {
      return [];
    }

    let filteredJobData = processedData;
    if (filterTerms.length > 0) {
      filteredJobData = filteredJobData.filter(jobPost =>
        filterTerms.every(({ value }) =>
          (Object.values(JobPostStatus) as string[]).includes(value)
            ? jobPost.status === value
            : value === JobPostType.Crackless
            ? !!jobPost.signupFlow
            : value === JobPostType.Legacy
            ? !jobPost.signupFlow
            : `${jobPost.pod}s`.toLowerCase().includes(value.toLowerCase()) ||
              jobPost.ocpFlow?.toLowerCase() === value.toLowerCase(),
        ),
      );
    }

    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filteredJobData = filteredJobData.filter(jobPost => {
        return (
          jobPost.id.includes(searchTermLower) ||
          jobPost.name.toLowerCase().includes(searchTermLower) ||
          jobPost.signupFlow?.name.toLowerCase().includes(searchTermLower) ||
          (jobPost.pod && `${jobPost.pod}s`.toLowerCase().includes(searchTermLower)) ||
          jobPost.status.toLowerCase().includes(searchTermLower) ||
          jobPost.jobName.toLowerCase().includes(searchTermLower) ||
          jobPost.jobId.includes(searchTermLower) ||
          jobPost.ocpFlow?.toLowerCase().includes(searchTermLower)
        );
      });
    }
    return filteredJobData;
  }, [processedData, searchTerm, filterTerms]);

  return (
    <div className="p-8 flex flex-col gap-4">
      <div className="flex flex-row justify-between w-full gap-5 items-center">
        <div>
          <Typography.H4>Job Posts</Typography.H4>
          <Typography.Paragraph2>
            Manage jobs for the Outlier job board (not Outlier AI). If you need to add job posts to
            an <i>existing</i> job, click the internal job name to be redirected to Greenhouse,
            where you can add a job post and then edit it here.
          </Typography.Paragraph2>
        </div>
        <div className="min-w-[100px]">
          <Link to={Routes.JobPostSetup}>
            <Button size="lg" variant="primary">
              <BaseIcon contrastText edge="start" icon={['fas', 'plus']} />
              New Job Post
            </Button>
          </Link>
        </div>
      </div>
      <div className="flex items-center gap-5 w-full justify-between">
        <TextField
          className="w-[35%]"
          leadingIcon="search"
          id="search"
          value={searchTerm}
          onChange={(e: any) => {
            setSearchTerm(e.target.value);
          }}
          placeholder="Search by ID, job name, signup flow, status, or external job name..."
        />
        <FilterComponent
          filterMessage={
            filterTerms.length === 0 ? 'Filter' : filterTerms.map(term => term.label).join(', ')
          }
        >
          <SelectTag
            showTagId={false}
            value={filterTerms}
            onChange={setFilterTerms}
            multi={true}
            placeholder={'Filter by crackless/legacy, status, pod, or OCP flow'}
          />
        </FilterComponent>
      </div>
      <JobPostTable data={filteredData} isLoading={greenhouseJobPostDataLoading} />
    </div>
  );
};

export default JobPostPage;
